import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { needsApi, usersApi, workflowRequestsApi, stepsApi, candidatesApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import websocketManager from '../services/websocketManager';
import { transformNeedFromApi } from '../services/api';
import { useWebSocket } from '../hooks/useWebSocket';
import {
  applyCandidateEvent,
  applyCandidateSnapshot,
  applyStepEvent,
  applyStepSnapshot,
  buildScoringCache,
  normalizeStep,
  withoutContextualScoring,
} from './candidateCache';

export { normalizeStep } from './candidateCache';


const COLUMNS = [
  { id: 'no-candidate', title: 'No Candidate', color: 'bg-gray-100' },
  { id: 'candidate-identified', title: 'Candidate Identified', color: 'bg-blue-100' },
  { id: 'candidate-sent', title: 'Candidate Sent', color: 'bg-yellow-100' },
  { id: 'candidate-interview', title: 'Candidate Interview', color: 'bg-purple-100' },
  { id: 'go-nogo', title: 'GO/NOGO', color: 'bg-green-100' }
];

// Context
const SofteamboardContext = createContext();

// ✅ FIXED: Use function to create initial cache to ensure fresh object
const createInitialCache = () => ({
  needs: [],
  consultants: [],
  candidatesById: {},     // id → Candidate
  candidatesByNeed: {},   // needId → [candidateId]
  candidateSteps: {},     // "needId_candidateId" → [Step]
  scoringData: [],        // NEW: All scoring data from API
  scoringDataByNeed: {},  // NEW: needId → [scoringData]
  scoringDataByCandidate: {}, // NEW: candidateId → [scoringData]
  lastFetched: 0,
  initialized: false,
  needsRefresh: true
});

// ✅ FIXED: Create safe initial filters
const createInitialFilters = () => ({
  consultants: [],
  clients: [],
  priorities: [],
  search: ''
});

export const SofteamboardProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  
  // ✅ FIXED: Use function to create initial cache to ensure fresh object
  const [cache, setCache] = useState(createInitialCache);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(createInitialFilters);
  
  // UI state
  const [selectedNeed, setSelectedNeed] = useState(null);
  const [isProcessTrackerOpen, setIsProcessTrackerOpen] = useState(false);
  
  // ✅ FIXED: Single loading flag to prevent concurrent calls
  const initializedRef = useRef(false);
  const loadingRef = useRef(false);
  const dataSessionGenerationRef = useRef(0);
  const candidateRefreshGenerationRef = useRef({});
  const candidateLoaderRef = useRef(null);
  const stepRefreshGenerationRef = useRef({});
  const stepInflightRef = useRef(new Map());
  const stepLoaderRef = useRef(null);
  const realtimeGenerationRef = useRef(0);
  const needsLoaderRef = useRef(null);
  const needsReloadRequestedRef = useRef(false);

  // ✅ NEW: Add loading state for bulk operations
  const [bulkLoading, setBulkLoading] = useState(false);

  // ✅ NEW: Load all scoring data from API - DEFINE THIS FIRST
  const loadAllScoringData = useCallback(async () => {
    const sessionGeneration = dataSessionGenerationRef.current;
    try {
      console.log('🚀 Loading all scoring data from API...');
      
      // Use the dedicated candidatesApi for scoring data
      const scoringResponse = await candidatesApi.getAllScoringData();
      if (sessionGeneration !== dataSessionGenerationRef.current) {
        return buildScoringCache([]);
      }
      const scoringData = scoringResponse.scoringData || [];
      
      // Process scoring data into organized structures
      const scoringDataByNeed = {};
      const scoringDataByCandidate = {};
      
      scoringData.forEach(scoring => {
        // Group by need
        if (!scoringDataByNeed[scoring.needId]) {
          scoringDataByNeed[scoring.needId] = [];
        }
        scoringDataByNeed[scoring.needId].push(scoring);
        
        // Group by candidate
        if (!scoringDataByCandidate[scoring.candidateId]) {
          scoringDataByCandidate[scoring.candidateId] = [];
        }
        scoringDataByCandidate[scoring.candidateId].push(scoring);
      });
      
      // Update cache with scoring data
      setCache(prev => ({
        ...prev,
        scoringData,
        scoringDataByNeed,
        scoringDataByCandidate
      }));
      
      console.log('✅ Loaded scoring data:', {
        total: scoringData.length,
        needsWithScoring: Object.keys(scoringDataByNeed).length,
        candidatesWithScoring: Object.keys(scoringDataByCandidate).length
      });
      
      return {
        scoringData,
        scoringDataByNeed,
        scoringDataByCandidate
      };
    } catch (error) {
      if (sessionGeneration !== dataSessionGenerationRef.current) {
        return buildScoringCache([]);
      }
      console.error('❌ Error loading scoring data:', error);
      throw error;
    }
  }, []);

  // ✅ FIXED: Don't mutate global candidate objects with scoring data
  // Scoring data should be kept separate and looked up contextually
  const applyScoringDataToCandidates = useCallback(() => {
    // This function is now deprecated but kept for backward compatibility
    // Scoring data is now accessed via getScoringDataForCandidateNeed()
    console.log('⚠️ applyScoringDataToCandidates is deprecated - scoring data is accessed via lookup functions');
    return;
  }, []);

  // ✅ OPTIMIZED: Load candidates only, defer steps to lazy loading
  const loadAllCandidatesAndSteps = useCallback(async () => {
    // Prevent concurrent calls
    if (bulkLoading) {
        console.log('🚀 Bulk load already in progress, skipping...');
        return;
    }

    const sessionGeneration = dataSessionGenerationRef.current;
    try {
        setBulkLoading(true);
        console.log('🚀 Bulk loading all candidates (steps deferred to lazy loading)...');

        // Load all candidates in one call - use the main api.js candidatesApi
        const allCandidates = await candidatesApi.getAllCandidates();
        if (sessionGeneration !== dataSessionGenerationRef.current) {
          return { candidates: [], steps: [] };
        }

        // Process candidates
        const candidatesById = {};
        const candidatesByNeed = {};

        allCandidates.forEach(candidate => {
            // Store candidate by ID
            candidatesById[candidate.id] = withoutContextualScoring(candidate);

            // Group candidates by need
            if (candidate.needs && Array.isArray(candidate.needs)) {
                candidate.needs.forEach(need => {
                    if (!candidatesByNeed[need.needId]) {
                        candidatesByNeed[need.needId] = [];
                    }
                    candidatesByNeed[need.needId].push(candidate.id);
                });
            }
        });

        // Update cache with candidates only (steps loaded on-demand)
        setCache(prev => ({
            ...prev,
            candidatesById,
            candidatesByNeed,
            lastFetched: Date.now()
        }));

        console.log('✅ Bulk load complete:', {
            candidates: Object.keys(candidatesById).length,
            needsWithCandidates: Object.keys(candidatesByNeed).length
        });

        return {
            candidates: allCandidates,
            steps: [] // Steps will be loaded lazily
        };
    } catch (err) {
        if (sessionGeneration !== dataSessionGenerationRef.current) {
          return { candidates: [], steps: [] };
        }
        console.error('❌ Error in bulk load:', err);
        throw err;
    } finally {
        if (sessionGeneration === dataSessionGenerationRef.current) {
          setBulkLoading(false);
        }
    }
}, [bulkLoading]);

  // ✅ NEW: Bulk refresh function - NOW CAN REFERENCE BOTH FUNCTIONS
  const refreshAllData = useCallback(async () => {
    const sessionGeneration = dataSessionGenerationRef.current;
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Refreshing all data including scoring...');
      
      // Load everything in parallel
      const [needsResponse, candidatesAndSteps, scoringData] = await Promise.all([
        needsApi.listAllNeeds(),
        loadAllCandidatesAndSteps(),
        loadAllScoringData()
      ]);
      if (sessionGeneration !== dataSessionGenerationRef.current) return null;
      
      const needsData = needsResponse.items || [];
      
      setCache(prev => ({
        ...prev,
        needs: needsData,
        initialized: true,
        lastFetched: Date.now()
      }));
      
      // Apply scoring data to candidates
      applyScoringDataToCandidates();
      
      console.log('✅ All data refreshed successfully including scoring');
      
      return {
        needs: needsData,
        candidates: candidatesAndSteps.candidates,
        steps: candidatesAndSteps.steps,
        scoringData: scoringData.scoringData
      };
    } catch (err) {
      if (sessionGeneration !== dataSessionGenerationRef.current) return null;
      setError(err.message || 'Failed to refresh all data');
      throw err;
    } finally {
      if (sessionGeneration === dataSessionGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [loadAllCandidatesAndSteps, loadAllScoringData, applyScoringDataToCandidates]);

  // ✅ MODIFIED: Enhanced initial load to prevent duplicate calls - NOW CAN REFERENCE ALL FUNCTIONS
  const loadNeeds = useCallback(async () => {
    // ✅ FIX: Prevent concurrent calls
    if (loadingRef.current) {
      console.log('🚀 Load needs already in progress, skipping...');
      return cache.needs || [];
    }

    const sessionGeneration = dataSessionGenerationRef.current;
    const realtimeGeneration = realtimeGenerationRef.current;
    needsReloadRequestedRef.current = false;
    try {
        const startTime = performance.now();
        loadingRef.current = true;
        setLoading(true);
        setError(null);

        const response = await needsApi.listAllNeeds();
        if (sessionGeneration !== dataSessionGenerationRef.current) return [];
        const needsData = response.items || [];

        console.log(`✅ Needs API loaded: ${needsData.length} needs in ${Math.round(performance.now() - startTime)}ms`);

        // ✅ PERFORMANCE OPTIMIZATION: Bulk load candidates for all needs
        // This eliminates the N+1 query pattern (21 sequential calls → 1 bulk call)
        console.log('⏳ Loading candidates in bulk...');

        const needIds = needsData.map(need => need.id);
        let candidatesById = {};
        let candidatesByNeed = {};
        let candidateSteps = {};
        let scoringData = []; // ✅ Initialize outside try block for proper scope
        let candidatesLoaded = false;

        if (needIds.length > 0) {
          try {
            const candidatesStart = performance.now();
            const bulkData = await candidatesApi.bulkLoadCandidates(needIds);
            const candidatesTime = performance.now() - candidatesStart;

            // Process bulk data
            Object.entries(bulkData).forEach(([needId, candidates]) => {
              candidatesByNeed[needId] = candidates.map(c => c.id);
              candidates.forEach(candidate => {
                candidatesById[candidate.id] = withoutContextualScoring(candidate);

                // ✅ Extract scoring data if present
                if (candidate.relevanceScore != null || candidate.relevance_score != null) {
                  const score = candidate.relevanceScore ?? candidate.relevance_score;
                  scoringData.push({
                    candidateId: candidate.id,
                    needId: needId,
                    score: score,
                    justification: candidate.scoreJustification ?? candidate.score_justification,
                    scoreJobId: candidate.scoreJobId ?? candidate.score_job_id,
                    scoredAt: candidate.scoredAt ?? candidate.scored_at
                  });
                }
              });
            });
            candidatesLoaded = true;

            const totalCandidates = Object.values(candidatesByNeed).flat().length;
            console.log(`✅ Candidates bulk loaded: ${totalCandidates} candidates in ${Math.round(candidatesTime)}ms`);
            console.log(`✅ Extracted ${scoringData.length} scoring records from candidates`);

            // ✅ PERFORMANCE OPTIMIZATION: Bulk load steps for all candidates
            // This eliminates the N+1 query pattern (1000+ sequential calls → 1 bulk call)
            console.log('⏳ Loading steps in bulk...');

            // Build list of need-candidate pairs
            const pairs = [];
            Object.entries(bulkData).forEach(([needId, candidates]) => {
              candidates.forEach(candidate => {
                pairs.push({ needId, candidateId: candidate.id });
              });
            });

            if (pairs.length > 0) {
              try {
                const stepsStart = performance.now();
                const stepsData = await stepsApi.bulkLoadSteps(pairs);
                const stepsTime = performance.now() - stepsStart;

                // Process steps data - stepsData is { "needId_candidateId": [steps...] }
                Object.entries(stepsData).forEach(([key, steps]) => {
                  candidateSteps[key] = steps.map(normalizeStep);
                });

                const totalSteps = Object.values(candidateSteps).flat().length;
                console.log(`✅ Steps bulk loaded: ${totalSteps} steps for ${pairs.length} candidates in ${Math.round(stepsTime)}ms`);
              } catch (stepsErr) {
                console.warn('⚠️ Failed to bulk load steps, will load on-demand:', stepsErr);
              }
            }

          } catch (candidatesErr) {
            console.warn('⚠️ Failed to bulk load candidates, will load on-demand:', candidatesErr);
          }
        }

        // ✅ FIX: Build scoring data maps for efficient lookup
        const scoringCache = buildScoringCache(scoringData);
        if (sessionGeneration !== dataSessionGenerationRef.current) return [];
        if (realtimeGeneration !== realtimeGenerationRef.current) {
          needsReloadRequestedRef.current = true;
          return [];
        }

        // ✅ FIX: Set cache with needs, candidates, steps AND scoring data together
        // NOTE: initialized is NOT set here - it's set by the coordinated initial load
        // after both needs AND consultants are loaded, preventing badges from appearing late
        setCache(prev => ({
            ...prev,
            needs: needsData,
            lastFetched: Date.now(),
            ...(candidatesLoaded && {
              candidatesById: { ...prev.candidatesById, ...candidatesById },
              candidatesByNeed,
              scoringData: scoringCache.scoringData,
              scoringDataByNeed: scoringCache.scoringDataByNeed,
              scoringDataByCandidate: scoringCache.scoringDataByCandidate,
            }),
            candidateSteps: { ...prev.candidateSteps, ...candidateSteps },
        }));

        const totalTime = performance.now() - startTime;
        console.log(`✅ loadNeeds complete: ${needsData.length} needs + candidates + steps + scoring in ${Math.round(totalTime)}ms`);

        return needsData;
    } catch (err) {
        if (sessionGeneration !== dataSessionGenerationRef.current) return [];
        setError(err.message || 'Failed to load needs');
        throw err;
    } finally {
        if (sessionGeneration === dataSessionGenerationRef.current) {
          setLoading(false);
          loadingRef.current = false;
          if (needsReloadRequestedRef.current) {
            setTimeout(() => {
              if (sessionGeneration === dataSessionGenerationRef.current) needsLoaderRef.current?.();
            }, 0);
          }
        }
    }
}, [cache.needs]);
  needsLoaderRef.current = loadNeeds;

  // ✅ OPTIMIZED: Load consultants with caching (5-minute TTL)
  const loadConsultants = useCallback(async () => {
    const sessionGeneration = dataSessionGenerationRef.current;
    try {
      const CONSULTANTS_CACHE_KEY = 'softeam_consultants_cache';
      const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

      // Check cache first
      const cachedData = localStorage.getItem(CONSULTANTS_CACHE_KEY);
      if (cachedData) {
        try {
          const { data, timestamp } = JSON.parse(cachedData);
          const age = Date.now() - timestamp;

          if (age < CACHE_TTL) {
            if (sessionGeneration !== dataSessionGenerationRef.current) return [];
            console.log(`✅ Using cached consultants (${Math.round(age / 1000)}s old)`);
            setCache(prev => ({
              ...prev,
              consultants: data
            }));
            return data;
          }
        } catch (e) {
          console.warn('⚠️ Invalid consultants cache, fetching fresh data');
        }
      }

      // Fetch from API
      const startTime = performance.now();
      const response = await usersApi.listSalesUsers({ limit: 100 });
      if (sessionGeneration !== dataSessionGenerationRef.current) return [];
      const fetchTime = performance.now() - startTime;

      const consultants = Array.isArray(response.items) ? response.items : [];

      console.log(`✅ Consultants loaded from API: ${consultants.length} consultants in ${Math.round(fetchTime)}ms`);

      // Update cache
      localStorage.setItem(CONSULTANTS_CACHE_KEY, JSON.stringify({
        data: consultants,
        timestamp: Date.now()
      }));

      setCache(prev => ({
        ...prev,
        consultants
      }));

      return consultants;
    } catch (err) {
      if (sessionGeneration !== dataSessionGenerationRef.current) return [];
      console.error('❌ Failed to load consultants:', err);
      throw err;
    }
  }, []);

  // ✅ NEW: Force refresh function that completely resets cache like browser refresh
  const forceRefreshAllData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Force refreshing all data (full reset like browser refresh)...');

      // ✅ FIX: Close any open modals
      setSelectedNeed(null);
      setIsProcessTrackerOpen(false);

      // ✅ FIX: Reset filters to initial state
      setFilters(createInitialFilters());

      // ✅ FIX: Completely clear ALL cache to match browser refresh behavior
      setCache({
        needs: [],
        consultants: [],
        initialized: false, // ✅ CRITICAL: Reset initialized flag
        candidatesById: {},
        candidatesByNeed: {},
        candidateSteps: {},
        scoringData: [],
        scoringDataByNeed: {},
        scoringDataByCandidate: {},
        lastFetched: 0
      });

      // ✅ FIX: Wait a moment for cache to clear before reloading
      await new Promise(resolve => setTimeout(resolve, 50));

      // ✅ FIX: Load everything fresh just like browser refresh (needs + consultants)
      await Promise.all([
        loadNeeds(),
        loadConsultants()
      ]);

      // ✅ FIX: Set initialized=true AFTER both loads complete
      // This ensures UI renders once with complete data (needs + consultants)
      // Prevents badges from appearing late after cards render
      setCache(prev => ({
        ...prev,
        initialized: true
      }));

      console.log('✅ Force refresh complete (full reset) - data reloaded');

      return;
    } catch (err) {
      setError(err.message || 'Failed to force refresh data');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadNeeds, loadConsultants]);

  // ✅ NEW: Lazy load scoring data for a specific need (only when user expands/views)
  const loadScoringForNeed = useCallback(async (needId) => {
    // Check if already loaded
    if (cache.scoringDataByNeed[needId]) {
      console.log(`✅ Scoring data already cached for need ${needId}`);
      return cache.scoringDataByNeed[needId];
    }

    try {
      console.log(`🚀 Lazy loading scoring data for need ${needId}...`);

      const scoringResponse = await candidatesApi.getScoringDataByNeed(needId);
      const scoringData = scoringResponse.scoringData || [];

      setCache(prev => ({
        ...prev,
        scoringDataByNeed: {
          ...prev.scoringDataByNeed,
          [needId]: scoringData
        }
      }));

      console.log(`✅ Loaded ${scoringData.length} scoring records for need ${needId}`);
      return scoringData;
    } catch (error) {
      console.error(`Error loading scoring for need ${needId}:`, error);
      return [];
    }
  }, [cache.scoringDataByNeed]);

  // ✅ NEW: Lazy load steps for a specific need-candidate combo
  const loadStepsForCombo = useCallback(async (needId, candidateId) => {
    const key = `${needId}_${candidateId}`;

    // Check if already loaded
    if (cache.candidateSteps[key]) {
      console.log(`✅ Steps already cached for ${key}`);
      return cache.candidateSteps[key];
    }

    try {
      console.log(`🚀 Lazy loading steps for ${key}...`);

      const response = await stepsApi.getSteps(needId, candidateId);
      const steps = response.steps || [];

      setCache(prev => ({
        ...prev,
        candidateSteps: {
          ...prev.candidateSteps,
          [key]: steps
        }
      }));

      console.log(`✅ Loaded ${steps.length} steps for ${key}`);
      return steps;
    } catch (error) {
      console.error(`Error loading steps for ${key}:`, error);
      return [];
    }
  }, [cache.candidateSteps]);

  // ✅ NEW: Get scoring data for a specific need
  const getScoringDataForNeed = useCallback((needId) => {
    return cache.scoringDataByNeed[needId] || [];
  }, [cache.scoringDataByNeed]);

  // ✅ NEW: Update candidate scoring data in store
  const updateCandidateScoringData = useCallback((needId, candidateId, scoringData) => {
    console.log('🔌 Store - Updating candidate scoring data:', {
      needId,
      candidateId,
      score: scoringData.score,
      hasJustification: !!scoringData.justification
    });
    
    setCache(prev => {
      const updatedScoringData = [...(prev.scoringData || [])];
      
      // Remove existing scoring data for this candidate-need combination
      const filteredScoringData = updatedScoringData.filter(
        scoring => !(scoring.candidateId === candidateId && scoring.needId === needId)
      );
      
      // Add new scoring data
      const newScoringData = {
        candidateId,
        needId,
        score: scoringData.score,
        justification: scoringData.justification,
        scoreJobId: scoringData.scoreJobId,
        scoredAt: scoringData.scoredAt,
        updatedAt: new Date().toISOString()
      };
      
      const finalScoringData = [...filteredScoringData, newScoringData];
      
      // Rebuild scoring data maps
      const scoringDataByNeed = {};
      const scoringDataByCandidate = {};
      
      finalScoringData.forEach(scoring => {
        if (!scoringDataByNeed[scoring.needId]) {
          scoringDataByNeed[scoring.needId] = [];
        }
        scoringDataByNeed[scoring.needId].push(scoring);
        
        if (!scoringDataByCandidate[scoring.candidateId]) {
          scoringDataByCandidate[scoring.candidateId] = [];
        }
        scoringDataByCandidate[scoring.candidateId].push(scoring);
      });
      
      return {
        ...prev,
        scoringData: finalScoringData,
        scoringDataByNeed,
        scoringDataByCandidate
      };
    });
  }, []);

  // ✅ NEW: Get scoring data for a specific candidate
  const getScoringDataForCandidate = useCallback((candidateId) => {
    return cache.scoringDataByCandidate[candidateId] || [];
  }, [cache.scoringDataByCandidate]);

  // ✅ NEW: Get scoring data for a specific candidate-need combination
  const getScoringDataForCandidateNeed = useCallback((candidateId, needId) => {
    const scoringData = cache.scoringData || [];
    return scoringData.find(scoring => 
      scoring.candidateId === candidateId && scoring.needId === needId
    ) || null;
  }, [cache.scoringData]);

  // ✅ NEW: Refresh scoring data for a specific need
  const refreshScoringDataForNeed = useCallback(async (needId) => {
    try {
      // Use the dedicated candidatesApi for scoring data
      const response = await candidatesApi.getScoringDataByNeed(needId);
      const scoringData = response.scoringData || [];
      
      setCache(prev => {
        const updatedScoringData = [...(prev.scoringData || [])];
        
        // Remove existing scoring data for this need
        const filteredScoringData = updatedScoringData.filter(
          scoring => scoring.needId !== needId
        );
        
        // Add new scoring data
        const newScoringData = [...filteredScoringData, ...scoringData];
        
        // Rebuild scoring data maps
        const scoringDataByNeed = {};
        const scoringDataByCandidate = {};
        
        newScoringData.forEach(scoring => {
          if (!scoringDataByNeed[scoring.needId]) {
            scoringDataByNeed[scoring.needId] = [];
          }
          scoringDataByNeed[scoring.needId].push(scoring);
          
          if (!scoringDataByCandidate[scoring.candidateId]) {
            scoringDataByCandidate[scoring.candidateId] = [];
          }
          scoringDataByCandidate[scoring.candidateId].push(scoring);
        });
        
        return {
          ...prev,
          scoringData: newScoringData,
          scoringDataByNeed,
          scoringDataByCandidate
        };
      });
      
      // Apply updated scoring data to candidates
      applyScoringDataToCandidates();
      
      return scoringData;
    } catch (error) {
      console.error(`❌ Error refreshing scoring data for need ${needId}:`, error);
      throw error;
    }
  }, [applyScoringDataToCandidates]);

  // ✅ NEW: Refresh scoring data for a specific candidate
  const refreshScoringDataForCandidate = useCallback(async (candidateId) => {
    try {
      // Use the dedicated candidatesApi for scoring data
      const response = await candidatesApi.getScoringDataByCandidate(candidateId);
      const scoringData = response.scoringData || [];
      
      setCache(prev => {
        const updatedScoringData = [...(prev.scoringData || [])];
        
        // Remove existing scoring data for this candidate
        const filteredScoringData = updatedScoringData.filter(
          scoring => scoring.candidateId !== candidateId
        );
        
        // Add new scoring data
        const newScoringData = [...filteredScoringData, ...scoringData];
        
        // Rebuild scoring data maps
        const scoringDataByNeed = {};
        const scoringDataByCandidate = {};
        
        newScoringData.forEach(scoring => {
          if (!scoringDataByNeed[scoring.needId]) {
            scoringDataByNeed[scoring.needId] = [];
          }
          scoringDataByNeed[scoring.needId].push(scoring);
          
          if (!scoringDataByCandidate[scoring.candidateId]) {
            scoringDataByCandidate[scoring.candidateId] = [];
          }
          scoringDataByCandidate[scoring.candidateId].push(scoring);
        });
        
        return {
          ...prev,
          scoringData: newScoringData,
          scoringDataByNeed,
          scoringDataByCandidate
        };
      });
      
      // Apply updated scoring data to candidates
      applyScoringDataToCandidates();
      
      return scoringData;
    } catch (error) {
      console.error(`❌ Error refreshing scoring data for candidate ${candidateId}:`, error);
      throw error;
    }
  }, [applyScoringDataToCandidates]);

  // ✅ FIXED: Create need with optimistic update that works with WebSocket
  const createNeed = useCallback(async (needData) => {
    try {
      setLoading(true);
      setError(null);
      
      const newNeed = await needsApi.createNeed(needData);
      
      // ✅ FIXED: Add to cache immediately with safety check
      setCache(prev => ({
        ...prev,
        needs: [newNeed, ...(prev.needs || []).filter(need => need.id !== newNeed.id)]
      }));
      
      return newNeed;
    } catch (err) {
      setError(err.message || 'Failed to create need');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ FIXED: Update need with proper safety checks
  const updateNeed = useCallback(async (needId, updateData) => {
    try {
      setLoading(true);
      setError(null);
      
      const updatedNeed = await needsApi.updateNeed(needId, updateData);
      
      setCache(prev => ({
        ...prev,
        needs: (prev.needs || []).map(need => 
          need.id === needId ? updatedNeed : need
        )
      }));
      
      return updatedNeed;
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ MODIFIED: Enhanced WebSocket handler to handle scoring updates
  const lastEventTimestampsRef = useRef({}); // entityId → ISO timestamp
  const handleWebSocketMessage = useCallback(async (message) => {
    const sessionGeneration = dataSessionGenerationRef.current;
    // ✅ FIXED: Add safety checks for message format
    if (!message || typeof message !== 'object') {
      return;
    }
    if (message.type === 'connection_test') {
      needsReloadRequestedRef.current = true;
      await needsLoaderRef.current?.();
      return;
    }
    if (/^(need|candidate|step)_(created|updated|deleted|merged)$/.test(message.event_type || '')) {
      realtimeGenerationRef.current += 1;
    }
    // ✅ NEW: Stale guard - ignore events older than the last seen for the same entity
    const entityId = message.entity_id || message.payload?.id;
    const ts = message.timestamp || message.payload?.updatedAt || message.payload?.timestamp;
    if (entityId && ts) {
      const lastTs = lastEventTimestampsRef.current[entityId];
      if (lastTs && new Date(ts) < new Date(lastTs)) {
        // Distinct updates can share a millisecond; the transport deduplicates messages.
        return;
      }
      lastEventTimestampsRef.current[entityId] = ts;
    }

    // ✅ FIXED: Skip connection test messages
    if (message.type === 'connection_test') {
      return; // Silently ignore connection test messages
    }

    // ✅ CRITICAL FIX: Handle need_created with full data fetch
    if (['need_created', 'need_updated'].includes(message.event_type) && entityId) {
      try {
        console.log('🔌 Fetching full need data for ID:', entityId);
        
        // Fetch complete need data from API
        const fullNeedData = await needsApi.getNeed(entityId);
        if (sessionGeneration !== dataSessionGenerationRef.current ||
            (ts && lastEventTimestampsRef.current[entityId] !== ts)) return;
        
        console.log('🔌 Full need data received:', {
          id: fullNeedData.id,
          title: fullNeedData.title,
          column: fullNeedData.column,
          status: fullNeedData.status,
          allKeys: Object.keys(fullNeedData)
        });
        
        setCache(currentCache => {
          // ✅ FIX: Always add/update the need with full data, don't check if exists
          console.log('🔌 Adding/updating need with full data:', {
            title: fullNeedData.title,
            status: fullNeedData.status,
            column: fullNeedData.column,
            totalNeedsBefore: currentCache.needs?.length || 0
          });
          
          // Remove existing need if it exists, then add the complete one
          const filteredNeeds = (currentCache.needs || []).filter(need => need.id !== fullNeedData.id);
          const updatedCache = {
            ...currentCache,
            needs: [fullNeedData, ...filteredNeeds]
          };
          
          console.log('🔌 Updated cache:', {
            totalNeedsAfter: updatedCache.needs.length,
            newNeedInCache: updatedCache.needs.find(n => n.id === fullNeedData.id)?.title
          });
          
          return updatedCache;
        });
        
        return; // Exit early after handling need_created
      } catch (error) {
        if (sessionGeneration !== dataSessionGenerationRef.current) return;
        console.error('🔌 Failed to fetch full need data:', error);
        // Fallback to basic payload data if API call fails
      }
    }

    // ✅ CRITICAL FIX: Handle need_updated with proper field mapping but keep finished needs in cache
    if (message.event_type === 'need_updated' && message.entity_id) {
      console.log('🔌 Processing need_updated message:', {
        needId: message.entity_id,
        payload: message.payload,
        column: message.payload?.column
      });
      
      setCache((prev) => {
        const needs = prev.needs ?? [];
        const idx = needs.findIndex((n) => n.id === message.entity_id);
        if (idx === -1) return prev;                // not present

        const patch = { ...message.payload };

        // ↔️ Map column → status si nécessaire (logique existante réutilisée)
        if (patch.column && !patch.status) {
          const map = {
            NO_CANDIDATE: 'no-candidate',
            CANDIDATE_IDENTIFIED: 'candidate-identified',
            CANDIDATE_SENT: 'candidate-sent',
            CANDIDATE_INTERVIEW: 'candidate-interview',
            GO_NOGO: 'go-nogo',
            SALE: 'sold',
            MISSION_COMPLETED: 'mission-completed',
            CANCELED: 'canceled',
            DELETED: 'deleted'
          };
          patch.status = map[patch.column] ?? patch.column.toLowerCase().replace('_', '-');
          
          console.log('🔌 Mapped column to status:', {
            column: patch.column,
            status: patch.status
          });
        }

        // ✅ CRITICAL FIX: Filter out undefined values to prevent corruption
        const cleanPatch = Object.fromEntries(
          Object.entries(patch).filter(([key, value]) => value !== undefined)
        );

        const updated = { ...needs[idx], ...cleanPatch };
        const nextNeeds = needs.slice();
        nextNeeds[idx] = updated;
        
        console.log('🔌 Updated need in cache:', {
          id: updated.id,
          title: updated.title,
          oldColumn: needs[idx].column,
          newColumn: updated.column,
          oldStatus: needs[idx].status,
          newStatus: updated.status,
          oldProgressPct: needs[idx].progressPct,
          newProgressPct: updated.progressPct
        });
        
        // ✅ KEEP FINISHED NEEDS: Don't remove needs when they become finished
        // The selector hooks will filter them appropriately
        return { ...prev, needs: nextNeeds };
      });
      
      return; // Exit early after handling need_updated
    }

    // ✅ NEW: Handle workflow request updates
    if (message.event_type === 'workflow_request_updated' && message.entity_id) {
      console.log('🔌 Processing workflow_request_updated message:', {
        workflowRequestId: message.entity_id,
        payload: message.payload,
        status: message.payload?.status
      });
      
      setWorkflowRequests((prev) => {
        const requests = prev || [];
        const idx = requests.findIndex((wr) => wr.id === message.entity_id);
        if (idx === -1) return prev; // not present
        
        const patch = { ...message.payload };
        
        // ✅ CRITICAL FIX: Filter out undefined values to prevent corruption
        const cleanPatch = Object.fromEntries(
          Object.entries(patch).filter(([key, value]) => value !== undefined)
        );
        
        const updated = { ...requests[idx], ...cleanPatch };
        const nextRequests = requests.slice();
        nextRequests[idx] = updated;
        
        console.log('🔌 Updated workflow request in cache:', {
          id: updated.id,
          oldStatus: requests[idx].status,
          newStatus: updated.status,
          oldAttempts: requests[idx].attempts,
          newAttempts: updated.attempts
        });
        
        return nextRequests;
      });
      
      return; // Exit early after handling workflow_request_updated
    }

    // ✅ NEW: Handle workflow request triggers
    if (message.event_type === 'workflow_request_triggered' && message.entity_id) {
      console.log('🔌 Processing workflow_request_triggered message:', {
        workflowRequestId: message.entity_id,
        payload: message.payload,
        status: message.payload?.status
      });
      
      setWorkflowRequests((prev) => {
        const requests = prev || [];
        const idx = requests.findIndex((wr) => wr.id === message.entity_id);
        if (idx === -1) return prev; // not present
        
        const patch = { ...message.payload };
        
        // ✅ CRITICAL FIX: Filter out undefined values to prevent corruption
        const cleanPatch = Object.fromEntries(
          Object.entries(patch).filter(([key, value]) => value !== undefined)
        );
        
        const updated = { ...requests[idx], ...cleanPatch };
        const nextRequests = requests.slice();
        nextRequests[idx] = updated;
        
        console.log('🔌 Triggered workflow request in cache:', {
          id: updated.id,
          oldStatus: requests[idx].status,
          newStatus: updated.status,
          attempts: updated.attempts
        });
        
        return nextRequests;
      });
      
      return; // Exit early after handling workflow_request_triggered
    }

    // ✅ NEW: Handle scoring analysis completion with enhanced logic
    if (message.event_type === 'scoring_analysis_complete') {
      console.log('🔌 Store - Scoring analysis complete:', message);

      // Handle both message formats: broadcast_event (with payload) and job_subscribers (with data)
      const scoringData = message.payload || message.data;

      if (scoringData) {
        const candidateId = scoringData.candidate_id;
        const needId = scoringData.need_id;
        const score = scoringData.score;
        let justification = scoringData.justification;

        // Ensure justification is a string
        if (justification && typeof justification === 'object') {
          justification = JSON.stringify(justification, null, 2);
        }

        if (candidateId && needId && score !== undefined) {
          console.log('🔌 Store - Updating candidate scoring data:', {
            candidateId,
            needId,
            score,
            hasJustification: !!justification
          });

          // ✅ OPTIMIZED: Update scoring data cache (partial cache - only if need is loaded)
          setCache(prev => {
            // Initialize scoring maps if not present
            if (!prev.scoringDataByNeed) prev = { ...prev, scoringDataByNeed: {} };
            if (!prev.scoringDataByCandidate) prev = { ...prev, scoringDataByCandidate: {} };
            const updatedScoringData = [...(prev.scoringData || [])];
            
            // Remove existing scoring data for this candidate-need combination
            const filteredScoringData = updatedScoringData.filter(
              scoring => !(scoring.candidateId === candidateId && scoring.needId === needId)
            );
            
            // Add new scoring data
            const newScoringData = {
              candidateId,
              candidateName: scoringData.candidate_name || '',
              candidateEmail: scoringData.candidate_email || '',
              needId,
              needTitle: scoringData.need_title || '',
              needClient: scoringData.need_client || '',
              score,
              justification,
              scoreJobId: message.entity_id,
              scoredAt: message.timestamp,
              updatedAt: message.timestamp
            };
            
            const finalScoringData = [...filteredScoringData, newScoringData];
            
            // Rebuild scoring data maps
            const scoringDataByNeed = {};
            const scoringDataByCandidate = {};
            
            finalScoringData.forEach(scoring => {
              if (!scoringDataByNeed[scoring.needId]) {
                scoringDataByNeed[scoring.needId] = [];
              }
              scoringDataByNeed[scoring.needId].push(scoring);
              
              if (!scoringDataByCandidate[scoring.candidateId]) {
                scoringDataByCandidate[scoring.candidateId] = [];
              }
              scoringDataByCandidate[scoring.candidateId].push(scoring);
            });
            
            return {
              ...prev,
              scoringData: finalScoringData,
              scoringDataByNeed,
              scoringDataByCandidate
            };
          });
          
        }
      }
      
      return; // Exit early after handling scoring_analysis_complete
    }

    const eventPayload = message.payload || message.data || {};
    const eventNeedId = eventPayload.needId || eventPayload.need_id;
    if (['candidate_deleted', 'candidate_merged'].includes(message.event_type)) {
      needsReloadRequestedRef.current = true;
      void needsLoaderRef.current?.();
    }
    if (['candidate_created', 'candidate_updated'].includes(message.event_type) && eventNeedId) {
      candidateRefreshGenerationRef.current[eventNeedId] =
        (candidateRefreshGenerationRef.current[eventNeedId] || 0) + 1;
      if (eventPayload.action?.toUpperCase() === 'ATTACHED') {
        setTimeout(() => candidateLoaderRef.current?.(eventNeedId), 0);
      }
    }
    if (['step_created', 'step_updated', 'step_deleted'].includes(message.event_type) && eventNeedId) {
      candidateRefreshGenerationRef.current[eventNeedId] =
        (candidateRefreshGenerationRef.current[eventNeedId] || 0) + 1;
      setTimeout(() => candidateLoaderRef.current?.(eventNeedId), 0);
      const eventCandidateId = eventPayload.candidateId || eventPayload.candidate_id;
      if (eventCandidateId) {
        const stepKey = `${eventNeedId}_${eventCandidateId}`;
        stepRefreshGenerationRef.current[stepKey] =
          (stepRefreshGenerationRef.current[stepKey] || 0) + 1;
        setTimeout(() => stepLoaderRef.current?.(eventNeedId, eventCandidateId, true), 0);
      }
    }

    // ✅ EXISTING: Handle other message types with existing logic
    setCache(currentCache => {
      switch (message.event_type) {
        case 'need_created':
          // ✅ FALLBACK: Only used if the API call above failed
          if (message.payload) {
            const newNeed = transformNeedFromApi ? transformNeedFromApi(message.payload) : message.payload;
            const exists = (currentCache.needs || []).some(need => need.id === newNeed.id);
            if (!exists) {
              console.log('🔌 Adding new need with basic data (fallback):', newNeed.title);
              const updatedNeeds = [newNeed, ...(currentCache.needs || [])];
              return {
                ...currentCache,
                needs: updatedNeeds
              };
            }
          }
          break;
        
        case 'candidate_created':
        case 'candidate_updated':
        case 'candidate_deleted':
        case 'candidate_merged':
          return applyCandidateEvent(currentCache, message);
        
        case 'step_created':
        case 'step_updated':
        case 'step_deleted':
          // ✅ FIX: Refresh both candidate and step cache
          {
            const payload = message.payload || message.data || {};
            const needId = payload.needId || payload.need_id;
            if (needId) {
              return applyStepEvent(currentCache, message);
            }
          }
          break;
        
        case 'need_deleted':
          // ✅ OPTIMIZED: Remove need and clear related data from cache
          const deletedNeedId = message.entity_id;
          console.log(`🔌 Removing need ${deletedNeedId} and related data from cache`);

          // Remove scoring data for this need
          const newScoringDataByNeed = { ...currentCache.scoringDataByNeed };
          delete newScoringDataByNeed[deletedNeedId];

          // Remove candidates for this need
          const newCandidatesByNeed = { ...currentCache.candidatesByNeed };
          delete newCandidatesByNeed[deletedNeedId];

          // Remove steps for this need
          const newCandidateSteps = { ...currentCache.candidateSteps };
          Object.keys(newCandidateSteps).forEach(key => {
            if (key.startsWith(`${deletedNeedId}_`)) {
              delete newCandidateSteps[key];
            }
          });

          return {
            ...currentCache,
            needs: (currentCache.needs || []).filter(need => need.id !== deletedNeedId),
            scoringDataByNeed: newScoringDataByNeed,
            candidatesByNeed: newCandidatesByNeed,
            candidateSteps: newCandidateSteps
          };
        
        default:
          return currentCache;
      }
      
      return currentCache;
    });
  }, []);


  // ✅ FIXED: Client-side filtering with comprehensive safety checks
  const filteredNeeds = useMemo(() => {
    const needs = cache.needs || [];
    
    // ✅ FIXED: Add safety check for filters object
    if (!filters) {
      return needs;
    }
    
    const filtered = needs.filter(need => {
      // ✅ FIXED: Add safety checks for need properties
      if (!need) return false;
      
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = 
          (need.title && need.title.toLowerCase().includes(searchLower)) ||
          (need.client && need.client.toLowerCase().includes(searchLower)) ||
          (need.description && need.description.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }

      // Consultant filter - ✅ FIXED: Add safety check for filters.consultants
      if (filters.consultants && filters.consultants.length > 0) {
        const needConsultants = need.consultants || [];
        const hasMatchingConsultant = needConsultants.some(consultantId =>
          filters.consultants.includes(consultantId)
        );
        if (!hasMatchingConsultant) return false;
      }

      // Client filter - ✅ FIXED: Add safety check for filters.clients
      if (filters.clients && filters.clients.length > 0) {
        if (!filters.clients.includes(need.client)) return false;
      }

      // Priority filter - ✅ FIXED: Add safety check for filters.priorities
      if (filters.priorities && filters.priorities.length > 0) {
        if (!filters.priorities.includes(need.priority)) return false;
      }

      return true;
    });
    
    return filtered;
  }, [cache.needs, filters]);

  // ✅ FIXED: Enhanced setFilters with safety checks
  const setFiltersWithSafety = useCallback((newFilters) => {
    setFilters(prev => ({
      consultants: [],
      clients: [],
      priorities: [],
      search: '',
      ...prev,
      ...newFilters
    }));
  }, []);

  // ✅ FIXED: Helper functions with safety checks
  const getUniqueClients = useCallback(() => {
    const needs = cache.needs || [];
    const clients = [...new Set(needs.map(need => need?.client).filter(Boolean))];
    return clients.sort();
  }, [cache.needs]);

  const getUniquePriorities = useCallback(() => {
    const needs = cache.needs || [];
    return [...new Set(needs.map(need => need?.priority).filter(Boolean))];
  }, [cache.needs]);

  const moveNeed = useCallback(async (needId, newStatus) => {
    try {
      setLoading(true);
      const updatedNeed = await needsApi.moveNeedColumn(needId, newStatus);
      
      setCache(prev => ({
        ...prev,
        needs: (prev.needs || []).map(need =>
          need.id === needId ? updatedNeed : need
        )
      }));
      
      return updatedNeed;
    } catch (err) {
      setError(err.message || 'Failed to move need');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ FIXED: Initial load ensures consultants loaded before UI renders
  useEffect(() => {
    if (isAuthenticated && !initializedRef.current && !loadingRef.current) {
      initializedRef.current = true;

      // ✅ FIX: Load needs and consultants in parallel, but coordinate initialization
      const initializeData = async () => {
        const sessionGeneration = dataSessionGenerationRef.current;
        try {
          const initStartTime = performance.now();
          console.log('🚀 Initial load started...');

          // Load both in parallel for optimal performance
          await Promise.all([
            loadNeeds(),
            loadConsultants()
          ]);
          if (sessionGeneration !== dataSessionGenerationRef.current) return;

          // ✅ FIX: Now set initialized=true AFTER both are loaded
          // This ensures UI renders once with complete data (needs + consultants)
          // Prevents sales badges from appearing late after cards render
          setCache(prev => ({
            ...prev,
            initialized: true
          }));

          const totalInitTime = performance.now() - initStartTime;
          console.log(`✅ Initial load complete in ${Math.round(totalInitTime)}ms - UI ready to render with all data`);
        } catch (err) {
          console.error('Error during initial load:', err);
          initializedRef.current = false; // Reset on error
        }
      };

      initializeData();
    }
  }, [isAuthenticated, loadNeeds, loadConsultants]); // Remove cache.initialized dependency

  // ✅ ADDED: Clear cache on logout
  useEffect(() => {
    if (!isAuthenticated) {
      dataSessionGenerationRef.current += 1;
      initializedRef.current = false;
      loadingRef.current = false;
      setLoading(false);
      setBulkLoading(false);
      websocketManager.disconnect();
      setCache(createInitialCache());
    }
  }, [isAuthenticated]);

  // ✅ FIXED: WebSocket subscription
  useWebSocket(localStorage.getItem('access_token'), handleWebSocketMessage);

  // === Candidate getters & loaders ===
  const getCandidate = useCallback(
    (id) => cache.candidatesById[id],
    [cache.candidatesById]
  );

  const getCandidatesForNeed = useCallback(
    (needId) => {
      const ids = cache.candidatesByNeed[needId] || [];
      return ids.map(id => cache.candidatesById[id]).filter(Boolean);
    }, [cache.candidatesByNeed, cache.candidatesById]
  );

  const loadCandidate = useCallback(async (id) => {
    const apiData = await candidatesApi.getCandidate(id);
    const cand = apiData;
    setCache(prev => ({
      ...prev,
      candidatesById: { ...prev.candidatesById, [id]: cand }
    }));
    return cand;
  }, []);

  const loadStepsForCandidate = useCallback(async (needId, candidateId, force = false) => {
    // ✅ FIX: Check if we already have steps from bulk loading
    const cacheKey = `${needId}_${candidateId}`;
    const cachedSteps = cache.candidateSteps[cacheKey];

    if (!force && cachedSteps !== undefined) {
      console.log(`📦 Using cached steps for ${needId}/${candidateId}:`, cachedSteps.length);
      return cachedSteps;
    }

    if (!force && stepInflightRef.current.has(cacheKey)) {
      return stepInflightRef.current.get(cacheKey);
    }

    const requestGeneration = (stepRefreshGenerationRef.current[cacheKey] || 0) + 1;
    stepRefreshGenerationRef.current[cacheKey] = requestGeneration;

    const promise = (async () => {
      try {
        console.log(`🔍 Loading steps for ${needId}/${candidateId} from API...`);
        const response = await candidatesApi.getStepsForProcess(needId, candidateId);
        const steps = response.steps || [];

        // ✅ ADD: Validate that steps belong to the correct need-candidate combination
        const validatedSteps = steps.filter(step => {
          const stepNeedId = step.needId || step.need_id;
          const stepCandidateId = step.candidateId || step.candidate_id;
          const isValid = stepNeedId === needId && stepCandidateId === candidateId;
          if (!isValid) {
            console.warn(`⚠️ Filtering out step ${step.id} with wrong need/candidate:`, {
              stepNeedId,
              stepCandidateId,
              expectedNeedId: needId,
              expectedCandidateId: candidateId
            });
          }
          return isValid;
        });

        console.log(`✅ Loaded ${validatedSteps.length} valid steps for ${needId}/${candidateId} (filtered from ${steps.length} total)`);

        const normalizedSteps = validatedSteps.map(normalizeStep);

        const isCurrent = requestGeneration === stepRefreshGenerationRef.current[cacheKey];
        setCache(prev => applyStepSnapshot(
          prev,
          cacheKey,
          normalizedSteps,
          requestGeneration,
          stepRefreshGenerationRef.current[cacheKey],
        ));
        return isCurrent ? normalizedSteps : null;
      } finally {
        if (stepInflightRef.current.get(cacheKey) === promise) {
          stepInflightRef.current.delete(cacheKey);
        }
      }
    })();

    stepInflightRef.current.set(cacheKey, promise);
    return promise;
  }, [cache.candidateSteps]);
  stepLoaderRef.current = loadStepsForCandidate;

  const getStepsForCandidate = useCallback(
    (needId, candidateId) => {
      const steps = cache.candidateSteps[`${needId}_${candidateId}`] || [];
      return steps.map(normalizeStep);
    },
    [cache.candidateSteps]
  );

  // Alias for compatibility
  const getStepsForProcess = getStepsForCandidate;
  const loadStepsForProcess = loadStepsForCandidate;

  const loadCandidatesForNeed = useCallback(async (needId) => {
    const requestGeneration = (candidateRefreshGenerationRef.current[needId] || 0) + 1;
    candidateRefreshGenerationRef.current[needId] = requestGeneration;
    try {
      // Check if we already have candidates in cache
      const cachedCandidates = cache.candidatesByNeed[needId];
      if (cachedCandidates && cachedCandidates.length > 0) {
        console.log(`📦 Using cached candidates for need ${needId}:`, cachedCandidates.length);
        return cachedCandidates.map(id => cache.candidatesById[id]).filter(Boolean);
      }
      
      // ✅ PERFORMANCE FIX: listNeedCandidates already returns full candidate objects
      // No need to fetch each candidate individually (N+1 query fix)
      console.log(`🔍 Loading candidates for need ${needId} from API...`);
      const fullCandidates = await candidatesApi.listNeedCandidates(needId) || [];

      // Update cache
      setCache(prev => applyCandidateSnapshot(
        prev,
        needId,
        fullCandidates,
        requestGeneration,
        candidateRefreshGenerationRef.current[needId],
      ));

      return requestGeneration === candidateRefreshGenerationRef.current[needId]
        ? fullCandidates.map(c => c)
        : null;
    } catch (err) {
      console.error('❌ Error loading candidates for need', needId, err);
      return [];
    }
  }, [cache.candidatesByNeed, cache.candidatesById]);

  const loadCandidatesWithStatusForNeed = useCallback(async (needId) => {
    const requestGeneration = (candidateRefreshGenerationRef.current[needId] || 0) + 1;
    candidateRefreshGenerationRef.current[needId] = requestGeneration;
    try {
      // Try the new API endpoint first
      const candidatesWithStatus = await candidatesApi.listNeedCandidatesWithStatus(needId);
      
      // Transform and cache the candidates
      setCache(prev => applyCandidateSnapshot(
        prev,
        needId,
        candidatesWithStatus,
        requestGeneration,
        candidateRefreshGenerationRef.current[needId],
      ));
      
      // Return the transformed candidates with status
      return requestGeneration === candidateRefreshGenerationRef.current[needId]
        ? candidatesWithStatus.map(c => c)
        : null;
    } catch (err) {
      console.warn('New API endpoint failed, falling back to old method:', err);
      if (requestGeneration !== candidateRefreshGenerationRef.current[needId]) return null;
      
      const fallbackCandidates = await candidatesApi.listNeedCandidates(needId) || [];
      setCache(prev => applyCandidateSnapshot(
        prev,
        needId,
        fallbackCandidates,
        requestGeneration,
        candidateRefreshGenerationRef.current[needId],
      ));
      return requestGeneration === candidateRefreshGenerationRef.current[needId]
        ? fallbackCandidates
        : null;
    }
  }, []);
  candidateLoaderRef.current = loadCandidatesWithStatusForNeed;

  // Add this new function to get candidates with status from cache
  const getCandidatesWithStatusForNeed = useCallback((needId) => {
    const candidateIds = cache.candidatesByNeed[needId] || [];
    return candidateIds.map(id => cache.candidatesById[id]).filter(Boolean);
  }, [cache.candidatesByNeed, cache.candidatesById]);


  const removeCandidateFromNeed = useCallback(async (needId, candidateId) => {
    await candidatesApi.removeCandidate(needId, candidateId);
    candidateRefreshGenerationRef.current[needId] =
      (candidateRefreshGenerationRef.current[needId] || 0) + 1;
    setCache(prev => ({
      ...prev,
      candidatesByNeed: {
        ...prev.candidatesByNeed,
        [needId]: (prev.candidatesByNeed[needId] || []).filter(id => id !== candidateId),
      }
    }));
  }, []);

  const downloadCv = useCallback(
    (candidateId) => candidatesApi.downloadCv(candidateId),
    []
  );

  // Add workflow state
  const [workflowRequests, setWorkflowRequests] = useState([]);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [workflowError, setWorkflowError] = useState(null);
  const [selectedWorkflowRequest, setSelectedWorkflowRequest] = useState(null);

  // Add workflow functions
  const loadWorkflowRequests = useCallback(async () => {
    try {
      setWorkflowLoading(true);
      setWorkflowError(null);
      const response = await workflowRequestsApi.listWorkflowRequests();
      setWorkflowRequests(Array.isArray(response.items) ? response.items : []);
      return response.items || [];
    } catch (err) {
      setWorkflowError(err.message || 'Failed to load workflow requests');
      throw err;
    } finally {
      setWorkflowLoading(false);
    }
  }, []);

  const createWorkflowRequest = useCallback(async (data) => {
    try {
      const result = await workflowRequestsApi.createWorkflowRequest(data);
      await loadWorkflowRequests();
      return result;
    } catch (err) {
      setWorkflowError(err.message || 'Failed to create workflow request');
      throw err;
    }
  }, [loadWorkflowRequests]);

  const triggerWorkflowRequest = useCallback(async (id, enhancedPayload = null) => {
    try {
      // ✅ FIX: Optimistic update - immediately set status to IN_PROGRESS
      setWorkflowRequests(prev => 
        prev.map(req => 
          req.id === id 
            ? { 
                ...req, 
                status: 'IN_PROGRESS', 
                payload: enhancedPayload || req.payload,
                flowId: null, // Clear old flow ID to trigger refresh
                updatedAt: new Date().toISOString()
              }
            : req
        )
      );

      // ✅ FIX: Send enhanced payload if provided
      const result = enhancedPayload 
        ? await workflowRequestsApi.updateWorkflowRequest(id, { payload: enhancedPayload }).then(() => 
            workflowRequestsApi.triggerWorkflowRequest(id)
          )
        : await workflowRequestsApi.triggerWorkflowRequest(id);

      // ✅ FIX: Only refresh if WebSocket is not connected (fallback)
      if (!websocketManager.getConnectionStatus()) {
        console.log('🔄 WebSocket not connected, falling back to manual refresh');
        await loadWorkflowRequests();
      }
      
      return result;
    } catch (err) {
      // ✅ FIX: Revert optimistic update on error
      setWorkflowRequests(prev => 
        prev.map(req => 
          req.id === id 
            ? { ...req, status: 'FAILED', lastError: err.message }
            : req
        )
      );
      setWorkflowError(err.message || 'Failed to trigger workflow request');
      throw err;
    }
  }, [loadWorkflowRequests]);

  const deleteWorkflowRequest = useCallback(async (id) => {
    try {
      await workflowRequestsApi.deleteWorkflowRequest(id);
      // Remove from local state
      setWorkflowRequests(prev => prev.filter(req => req.id !== id));
    } catch (err) {
      setWorkflowError(err.message || 'Failed to delete workflow request');
      throw err;
    }
  }, []);

  const cancelWorkflowRequest = useCallback(async (id) => {
    try {
      const result = await workflowRequestsApi.cancelWorkflowRequest(id);
      // Refresh the list after canceling
      await loadWorkflowRequests();
      return result;
    } catch (err) {
      setWorkflowError(err.message || 'Failed to cancel workflow request');
      throw err;
    }
  }, [loadWorkflowRequests]);

  const retryWorkflowRequest = useCallback(async (id) => {
    try {
      const result = await workflowRequestsApi.retryWorkflowRequest(id);
      // Refresh the list after retrying
      await loadWorkflowRequests();
      return result;
    } catch (err) {
      setWorkflowError(err.message || 'Failed to retry workflow request');
      throw err;
    }
  }, [loadWorkflowRequests]);

  const getWorkflowRequest = useCallback(async (id) => {
    try {
      setWorkflowLoading(true);
      setWorkflowError(null);
      const response = await workflowRequestsApi.getWorkflowRequest(id);
      setSelectedWorkflowRequest(response);
      return response;
    } catch (err) {
      setWorkflowError(err.message || 'Failed to load workflow request');
      throw err;
    } finally {
      setWorkflowLoading(false);
    }
  }, []);

  const updateWorkflowRequest = useCallback(async (id, updateData) => {
    try {
      const result = await workflowRequestsApi.updateWorkflowRequest(id, updateData);
      // Update the selected workflow request if it's the one being updated
      if (selectedWorkflowRequest?.id === id) {
        setSelectedWorkflowRequest(prev => ({ ...prev, ...result }));
      }
      // Also refresh the list
      await loadWorkflowRequests();
      return result;
    } catch (err) {
      setWorkflowError(err.message || 'Failed to update workflow request');
      throw err;
    }
  }, [selectedWorkflowRequest?.id, loadWorkflowRequests]);

  // ✅ NEW: Add candidate management functions to the store
  const addCandidateToNeed = useCallback(async (needId, candidateData) => {
    if (!needId) throw new Error('addCandidateToNeed: needId is required');
    if (!candidateData?.id) throw new Error('addCandidateToNeed: candidateData with id is required');

    const transformed = candidateData;
    setCache(prev => {
      const existingIds = prev.candidatesByNeed[needId] || [];
      if (existingIds.includes(candidateData.id)) return prev;
      return {
        ...prev,
        candidatesById: { ...prev.candidatesById, [candidateData.id]: transformed },
        candidatesByNeed: {
          ...prev.candidatesByNeed,
          [needId]: [...existingIds, candidateData.id]
        }
      };
    });

    return candidateData;
  }, []);

  // ✅ NEW: Force refresh candidates for a specific need (always hits API, bypasses cache)
  const refreshCandidatesForNeed = useCallback(
    (needId) => loadCandidatesWithStatusForNeed(needId),
    [loadCandidatesWithStatusForNeed]
  );

  // ✅ NEW: Bulk refresh candidates for multiple needs
  const refreshCandidatesForNeeds = useCallback(async (needIds) => {
    if (!needIds || needIds.length === 0) return {};
    
    try {
      console.log(`🔄 Refreshing candidates for ${needIds.length} needs...`);
      
      // Use bulk endpoint if available, otherwise fall back to individual calls
      try {
        const bulkData = await candidatesApi.bulkLoadCandidates(needIds);
        
        // Process bulk data
        const candidatesById = {};
        const candidatesByNeed = {};
        
        Object.entries(bulkData).forEach(([needId, candidates]) => {
          candidatesByNeed[needId] = candidates.map(c => c.id);
          candidates.forEach(candidate => {
            candidatesById[candidate.id] = candidate;
          });
        });
        
        setCache(prev => ({
          ...prev,
          candidatesById: { ...prev.candidatesById, ...candidatesById },
          candidatesByNeed: { ...prev.candidatesByNeed, ...candidatesByNeed }
        }));
        
        console.log(`✅ Bulk refresh complete for ${needIds.length} needs`);
        return candidatesByNeed;
      } catch (bulkErr) {
        console.warn('Bulk refresh failed, falling back to individual calls:', bulkErr);
        
        // Fallback to individual calls
        const candidatePromises = needIds.map(async (needId) => {
          try {
            const candidates = await candidatesApi.listNeedCandidates(needId);
            return { needId, candidates: candidates || [] };
          } catch (err) {
            console.warn(`Failed to refresh candidates for need ${needId}:`, err);
            return { needId, candidates: [] };
          }
        });
        
        const results = await Promise.all(candidatePromises);
        
        const newCandidatesByNeed = {};
        results.forEach(({ needId, candidates }) => {
          newCandidatesByNeed[needId] = candidates;
        });
        
        setCache(prev => ({
          ...prev,
          candidatesByNeed: {
            ...prev.candidatesByNeed,
            ...newCandidatesByNeed
          }
        }));
        
        return newCandidatesByNeed;
      }
    } catch (err) {
      console.error('❌ Error refreshing candidates for needs:', err);
      return {};
    }
  }, []);

  // ✅ NEW: Refresh candidates for all needs efficiently using bulk endpoint
  const refreshAllCandidates = useCallback(async (needIds) => {
    if (!needIds || needIds.length === 0) return {};

    try {
      console.log(`🔄 Bulk refreshing candidates for ${needIds.length} needs...`);

      // Use bulk endpoint for better performance
      try {
        const bulkData = await candidatesApi.bulkLoadCandidates(needIds);

        // Process bulk data
        const candidatesById = {};
        const candidatesByNeed = {};

        Object.entries(bulkData).forEach(([needId, candidates]) => {
          candidatesByNeed[needId] = candidates.map(c => c.id);
          candidates.forEach(candidate => {
            candidatesById[candidate.id] = candidate;
          });
        });

        setCache(prev => ({
          ...prev,
          candidatesById: { ...prev.candidatesById, ...candidatesById },
          candidatesByNeed: { ...prev.candidatesByNeed, ...candidatesByNeed }
        }));

        console.log(`✅ Bulk refresh complete for ${needIds.length} needs`);
        return candidatesByNeed;
      } catch (bulkErr) {
        console.warn('⚠️ Bulk endpoint failed, falling back to individual calls:', bulkErr);

        // Fallback to individual calls in parallel
        const candidatePromises = needIds.map(async (needId) => {
          try {
            const candidates = await candidatesApi.listNeedCandidates(needId);
            return { needId, candidates: candidates || [] };
          } catch (err) {
            console.warn(`Failed to refresh candidates for need ${needId}:`, err);
            return { needId, candidates: [] };
          }
        });

        const results = await Promise.all(candidatePromises);

        // Update cache with all results at once
        const newCandidatesById = {};
        const newCandidatesByNeed = {};
        results.forEach(({ needId, candidates }) => {
          newCandidatesByNeed[needId] = candidates.map(c => c.id);
          candidates.forEach(candidate => {
            newCandidatesById[candidate.id] = candidate;
          });
        });

        setCache(prev => ({
          ...prev,
          candidatesById: { ...prev.candidatesById, ...newCandidatesById },
          candidatesByNeed: { ...prev.candidatesByNeed, ...newCandidatesByNeed }
        }));

        console.log(`✅ Fallback refresh complete for ${needIds.length} needs`);
        return newCandidatesByNeed;
      }
    } catch (err) {
      console.error('❌ Error refreshing all candidates:', err);
      return {};
    }
  }, []);

  // ✅ NEW: Simple function to remove a need from cache
  const removeNeedFromCache = useCallback((needId) => {
    setCache(prev => ({
      ...prev,
      needs: (prev.needs || []).filter(need => need.id !== needId),
      candidatesByNeed: {
        ...prev.candidatesByNeed,
        [needId]: undefined // Remove candidates for this need
      }
    }));
  }, []);

  // ✅ NEW: Add function to update step cache when step is created
  const addStepToCache = useCallback((needId, candidateId, newStep) => {
    console.log('🔧 Adding step to cache:', { needId, candidateId, stepId: newStep.id, stepType: newStep.type });
    
    setCache(prev => {
      const cacheKey = `${needId}_${candidateId}`;
      const existingSteps = prev.candidateSteps[cacheKey] || [];
      
      // Check if step already exists to avoid duplicates
      const stepExists = existingSteps.some(step => step.id === newStep.id);
      if (stepExists) {
        return {
          ...prev,
          candidateSteps: {
            ...prev.candidateSteps,
            [cacheKey]: existingSteps.map(step => step.id === newStep.id
              ? normalizeStep({ ...step, ...newStep }) : step),
          },
        };
      }
      
      // Add new step to the beginning of the list (most recent first)
      const normalizedStep = normalizeStep(newStep);
      const updatedSteps = [normalizedStep, ...existingSteps];
      
      console.log(' Updated step cache:', {
        cacheKey,
        oldCount: existingSteps.length,
        newCount: updatedSteps.length,
        newStepType: normalizedStep.type
      });
      
      return {
        ...prev,
        candidateSteps: {
          ...prev.candidateSteps,
          [cacheKey]: updatedSteps
        }
      };
    });
  }, []);

  // ✅ NEW: Add function to update step cache when step is updated
  const updateStepInCache = useCallback((needId, candidateId, updatedStep) => {
    console.log('🔧 Updating step in cache:', { needId, candidateId, stepId: updatedStep.id, stepType: updatedStep.type });
    
    setCache(prev => {
      const cacheKey = `${needId}_${candidateId}`;
      const existingSteps = prev.candidateSteps[cacheKey] || [];
      
      const normalizedStep = normalizeStep(updatedStep);
      
      const updatedSteps = existingSteps.map(step => 
        step.id === normalizedStep.id ? normalizedStep : step
      );
      
      console.log('🔧 Updated step in cache:', {
        cacheKey,
        stepId: normalizedStep.id,
        oldType: existingSteps.find(s => s.id === normalizedStep.id)?.type,
        newType: normalizedStep.type
      });
      
      return {
        ...prev,
        candidateSteps: {
          ...prev.candidateSteps,
          [cacheKey]: updatedSteps
        }
      };
    });
  }, []);

  // ✅ NEW: Add function to remove step from cache when step is deleted
  const removeStepFromCache = useCallback((needId, candidateId, stepId) => {
    console.log(' Removing step from cache:', { needId, candidateId, stepId });
    
    setCache(prev => {
      const cacheKey = `${needId}_${candidateId}`;
      const existingSteps = prev.candidateSteps[cacheKey] || [];
      
      const updatedSteps = existingSteps.filter(step => step.id !== stepId);
      
      console.log('🔧 Removed step from cache:', {
        cacheKey,
        stepId,
        oldCount: existingSteps.length,
        newCount: updatedSteps.length
      });
      
      return {
        ...prev,
        candidateSteps: {
          ...prev.candidateSteps,
          [cacheKey]: updatedSteps
        }
      };
    });
  }, []);

  // ✅ ENHANCED: Provider value with new scoring data functions
  const value = useMemo(() => ({
    // Data with safety checks
    needs: cache.needs || [],
    filteredNeeds,
    consultants: cache.consultants || [],
    
    // ✅ ADD: Expose initialized flag
    initialized: cache.initialized || false,
    
    // ✅ ADD: Expose candidatesById for direct access
    candidatesById: cache.candidatesById || {},
    
    // State
    loading,
    error,
    filters: filters || createInitialFilters(),
    selectedNeed,
    isProcessTrackerOpen,
    
    // Actions
    setFilters: setFiltersWithSafety,
    setError,
    createNeed,
    updateNeed,
    moveNeed,
    loadNeeds,
    getUniqueClients,
    getUniquePriorities,
    
    // UI actions
    openProcessTracker: (needId) => {
      setSelectedNeed(needId);
      setIsProcessTrackerOpen(true);
    },
    closeProcessTracker: () => {
      setSelectedNeed(null);
      setIsProcessTrackerOpen(false);
    },
    
    // Legacy support
    columns: COLUMNS,
    refreshNeeds: loadNeeds,
    user,

    // Workflow
    workflowRequests,
    workflowLoading,
    workflowError,
    selectedWorkflowRequest,
    loadWorkflowRequests,
    createWorkflowRequest,
    getWorkflowRequest,
    triggerWorkflowRequest,
    updateWorkflowRequest,
    deleteWorkflowRequest,
    cancelWorkflowRequest,
    retryWorkflowRequest,
    setSelectedWorkflowRequest,
    
    // ✅ NEW: Enhanced candidate management
    addCandidateToNeed,
    refreshAllCandidates,
    getCandidate,
    loadCandidate,
    getCandidatesForNeed,
    loadCandidatesForNeed,
    refreshCandidatesForNeed,
    removeCandidateFromNeed,
    getStepsForCandidate,
    loadStepsForCandidate,
    getStepsForProcess,
    loadStepsForProcess,
    downloadCv,
    loadCandidatesWithStatusForNeed,
    getCandidatesWithStatusForNeed,
    
    // ✅ NEW: Bulk operations
    loadAllCandidatesAndSteps,
    refreshAllData,
    refreshCandidatesForNeeds,
    
    // ✅ NEW: Force refresh function
    forceRefreshAllData,
    
    // ✅ NEW: Add removeNeedFromCache function
    removeNeedFromCache,

    // ✅ NEW: Step cache management functions
    addStepToCache,
    updateStepInCache,
    removeStepFromCache,
    loadStepsForCombo, // ✅ NEW: Lazy loading for steps

    // ✅ NEW: Scoring data functions
    getScoringDataForNeed,
    getScoringDataForCandidate,
    getScoringDataForCandidateNeed,
    refreshScoringDataForNeed,
    refreshScoringDataForCandidate,
    loadAllScoringData,
    loadScoringForNeed, // ✅ NEW: Lazy loading for scoring
    applyScoringDataToCandidates,
    updateCandidateScoringData,
    
    // ✅ NEW: Expose scoring data in cache
    scoringData: cache.scoringData || [],
    scoringDataByNeed: cache.scoringDataByNeed || {},
    scoringDataByCandidate: cache.scoringDataByCandidate || {},
    
    // ✅ NEW: Expose step cache for dynamic updates
    candidateSteps: cache.candidateSteps || {},
  }), [
    cache.needs,
    cache.consultants,
    cache.initialized,
    cache.candidatesById,
    cache.candidatesByNeed,
    cache.lastFetched,
    filteredNeeds,
    loading,
    error,
    filters,
    selectedNeed,
    isProcessTrackerOpen,
    createNeed,
    updateNeed,
    moveNeed,
    loadNeeds,
    getCandidatesForNeed,
    loadCandidatesForNeed,
    getUniqueClients,
    getUniquePriorities,
    setFiltersWithSafety,
    user,
    workflowRequests,
    workflowLoading,
    workflowError,
    selectedWorkflowRequest,
    loadWorkflowRequests,
    createWorkflowRequest,
    getWorkflowRequest,
    triggerWorkflowRequest,
    updateWorkflowRequest,
    deleteWorkflowRequest,
    cancelWorkflowRequest,
    retryWorkflowRequest,
    setSelectedWorkflowRequest,
    
    // ✅ NEW: Enhanced candidate management
    addCandidateToNeed,
    refreshAllCandidates,
    getCandidate,
    loadCandidate,
    getCandidatesForNeed,
    loadCandidatesForNeed,
    refreshCandidatesForNeed,
    removeCandidateFromNeed,
    getStepsForCandidate,
    loadStepsForCandidate,
    getStepsForProcess,
    loadStepsForProcess,
    downloadCv,
    loadCandidatesWithStatusForNeed,
    getCandidatesWithStatusForNeed,
    
    // ✅ NEW: Bulk operations
    loadAllCandidatesAndSteps,
    refreshAllData,
    refreshCandidatesForNeeds,
    
    // ✅ NEW: Force refresh function
    forceRefreshAllData,
    
    // ✅ NEW: Add removeNeedFromCache function
    removeNeedFromCache,
    
    // ✅ NEW: Step cache management functions
    addStepToCache,
    updateStepInCache,
    removeStepFromCache,
    loadStepsForCombo, // ✅ NEW: Lazy loading dependency

    // ✅ NEW: Scoring data dependencies
    getScoringDataForNeed,
    getScoringDataForCandidate,
    getScoringDataForCandidateNeed,
    refreshScoringDataForNeed,
    refreshScoringDataForCandidate,
    loadAllScoringData,
    loadScoringForNeed, // ✅ NEW: Lazy loading dependency
    applyScoringDataToCandidates,
    updateCandidateScoringData,
    cache.scoringData,
    cache.scoringDataByNeed,
    cache.scoringDataByCandidate,
    cache.candidateSteps,  // ✅ NEW: Add step cache dependency
  ]);

  // ✅ ADD: Post-analysis modal state
  const [postAnalysisModal, setPostAnalysisModal] = useState({
    open: false,
    need: null,
    missingFields: [],
    requiredForWorkflows: []
  });

  return (
    <SofteamboardContext.Provider value={{ ...value, postAnalysisModal, setPostAnalysisModal }}>
      {children}
    </SofteamboardContext.Provider>
  );
};

// Export the hook
export const useSofteamboardStore = () => {
  const context = useContext(SofteamboardContext);
  if (!context) {
    throw new Error('useSofteamboardStore must be used within a SofteamboardProvider');
  }
  return context;
};

// ✅ ADD: Selector hooks for filtering needs
export const useActiveNeeds = () => {
  const { needs } = useSofteamboardStore();
  
  return useMemo(() => {
    // Filter out finished needs - exclude SALE, CANCELED, DELETED statuses
    const excludedStatuses = ['SALE', 'CANCELED', 'DELETED', 'sold', 'canceled', 'deleted'];
    return needs.filter(need => {
      const status = need.column || need.status;
      return !excludedStatuses.includes(status);
    });
  }, [needs]);
};

export const useFinishedNeeds = () => {
  const { needs } = useSofteamboardStore();
  
  return useMemo(() => {
    // Include only finished needs - SALE, CANCELED, DELETED statuses
    const finishedStatuses = ['SALE', 'CANCELED', 'DELETED', 'sold', 'canceled', 'deleted'];
    return needs.filter(need => {
      const status = need.column || need.status;
      return finishedStatuses.includes(status);
    });
  }, [needs]);
};
